<script setup lang="ts">
import { ref } from 'vue'
import {
  BookOpen,
  FileText,
  HelpCircle,
  Code,
  Sparkles,
  Calendar,
  Users,
  Newspaper,
} from 'lucide-vue-next'
import type { DocEntry, NewsEntry } from '@/types/landing'

interface Props {
  docs: DocEntry[]
  news: NewsEntry[]
}

const props = defineProps<Props>()

/** 首页文档 icon → 文档中心 slug 映射 */
const iconToSlug: Record<string, string> = {
  BookOpen: 'quickstart',
  FileText: 'guide',
  HelpCircle: 'faq',
  Code: 'api',
}

/** 根据标题关键词推断 slug */
const titleToSlug = [
  { keywords: ['快速入门', '入门'], slug: 'quickstart' },
  { keywords: ['指南', '手册', '使用', '功能'], slug: 'guide' },
  { keywords: ['FAQ', '常见', '问题'], slug: 'faq' },
  { keywords: ['API', '开发', '接口'], slug: 'api' },
]

function getDocSlug(doc: DocEntry): string {
  // 优先用 icon 映射
  if (iconToSlug[doc.icon]) return iconToSlug[doc.icon]
  // fallback：按标题关键词推断
  for (const item of titleToSlug) {
    if (item.keywords.some(k => doc.title.includes(k))) return item.slug
  }
  // 兜底：保持原 id（可能直接就是 slug）
  return doc.id
}

const iconMap: Record<string, any> = {
  BookOpen,
  FileText,
  HelpCircle,
  Code,
  Sparkles,
  Calendar,
  Users,
  Newspaper,
  news: Newspaper,
}

// 移动端 Tab 切换
const activeTab = ref<'docs' | 'news'>('docs')
</script>

<template>
  <section class="bg-[#F7F8FA] py-16 md:py-20">
    <div class="mx-auto max-w-[1200px] px-6">
      <!-- 桌面端：双列并排 -->
      <div class="hidden gap-8 md:grid md:grid-cols-2">
        <!-- 文档中心 -->
        <div id="docs" class="rounded-2xl bg-white p-6 md:p-8">
          <h3 class="mb-6 text-xl font-semibold text-[#1D2129]">文档中心</h3>
          <ul class="space-y-1">
            <li v-for="doc in props.docs" :key="doc.id">
              <RouterLink
                :to="`/docs/${getDocSlug(doc)}`"
                class="group flex items-center justify-between rounded-lg px-4 py-3 text-[#4E5969] no-underline transition-colors hover:bg-[#F7F8FA]"
              >
                <span class="flex items-center gap-3">
                  <component
                    :is="iconMap[doc.icon as keyof typeof iconMap]"
                    class="h-5 w-5 text-[#86909C] transition-colors group-hover:text-indigo-600"
                  />
                  <span class="text-sm">{{ doc.title }}</span>
                </span>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" class="text-[#C9CDD4] transition-colors group-hover:text-indigo-600">
                  <path d="M6 3l5 5-5 5" />
                </svg>
              </RouterLink>
            </li>
          </ul>
          <RouterLink
            to="/docs"
            class="mt-4 inline-flex items-center gap-1 text-sm font-medium text-indigo-600 no-underline hover:opacity-80"
          >
            查看全部文档
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
              <path d="M6 3l5 5-5 5" />
            </svg>
          </RouterLink>
        </div>

        <!-- 活动资讯 -->
        <div id="news" class="rounded-2xl bg-white p-6 md:p-8">
          <h3 class="mb-6 text-xl font-semibold text-[#1D2129]">活动资讯</h3>
          <ul class="space-y-1">
            <li v-for="item in props.news" :key="item.id">
              <RouterLink
                :to="`/news/${item.id}`"
                class="group flex items-start justify-between gap-4 rounded-lg px-4 py-3 text-[#4E5969] no-underline transition-colors hover:bg-[#F7F8FA]"
              >
                <span class="flex items-start gap-3">
                  <component
                    :is="iconMap[item.icon as keyof typeof iconMap]"
                    class="mt-0.5 h-5 w-5 flex-shrink-0 text-[#86909C] transition-colors group-hover:text-indigo-600"
                  />
                  <span class="text-sm">{{ item.title }}</span>
                </span>
                <span class="flex-shrink-0 text-xs text-[#C9CDD4]">{{ item.date }}</span>
              </RouterLink>
            </li>
          </ul>
          <RouterLink
            to="/news"
            class="mt-4 inline-flex items-center gap-1 text-sm font-medium text-indigo-600 no-underline hover:opacity-80"
          >
            查看全部资讯
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
              <path d="M6 3l5 5-5 5" />
            </svg>
          </RouterLink>
        </div>
      </div>

      <!-- 移动端：Tab 切换 -->
      <div class="md:hidden">
        <!-- Tab 栏 -->
        <div class="mb-6 flex rounded-xl bg-white p-1 shadow-sm" role="tablist">
          <button
            role="tab"
            :aria-selected="activeTab === 'docs'"
            class="flex-1 rounded-lg px-4 py-2.5 text-sm font-medium transition-all duration-200"
            :class="activeTab === 'docs' ? 'bg-indigo-600 text-white shadow-sm' : 'text-[#86909C]'"
            @click="activeTab = 'docs'"
          >
            文档中心
          </button>
          <button
            role="tab"
            :aria-selected="activeTab === 'news'"
            class="flex-1 rounded-lg px-4 py-2.5 text-sm font-medium transition-all duration-200"
            :class="activeTab === 'news' ? 'bg-indigo-600 text-white shadow-sm' : 'text-[#86909C]'"
            @click="activeTab = 'news'"
          >
            活动资讯
          </button>
        </div>

        <!-- Tab 内容 -->
        <div v-if="activeTab === 'docs'" class="rounded-2xl bg-white p-6">
          <h3 class="mb-6 text-lg font-semibold text-[#1D2129]">文档中心</h3>
          <ul class="space-y-1">
            <li v-for="doc in props.docs" :key="doc.id">
              <RouterLink
                :to="`/docs/${getDocSlug(doc)}`"
                class="group flex items-center justify-between rounded-lg px-4 py-3 text-[#4E5969] no-underline transition-colors hover:bg-[#F7F8FA]"
              >
                <span class="flex items-center gap-3">
                  <component
                    :is="iconMap[doc.icon as keyof typeof iconMap]"
                    class="h-5 w-5 text-[#86909C] transition-colors group-hover:text-indigo-600"
                  />
                  <span class="text-sm">{{ doc.title }}</span>
                </span>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" class="text-[#C9CDD4]">
                  <path d="M6 3l5 5-5 5" />
                </svg>
              </RouterLink>
            </li>
          </ul>
        </div>
        <div v-if="activeTab === 'news'" class="rounded-2xl bg-white p-6">
          <h3 class="mb-6 text-lg font-semibold text-[#1D2129]">活动资讯</h3>
          <ul class="space-y-1">
            <li v-for="item in props.news" :key="item.id">
              <RouterLink
                :to="`/news/${item.id}`"
                class="group flex items-start justify-between gap-4 rounded-lg px-4 py-3 text-[#4E5969] no-underline transition-colors hover:bg-[#F7F8FA]"
              >
                <span class="flex items-start gap-3">
                  <component
                    :is="iconMap[item.icon as keyof typeof iconMap]"
                    class="mt-0.5 h-5 w-5 flex-shrink-0 text-[#86909C] transition-colors group-hover:text-indigo-600"
                  />
                  <span class="text-sm">{{ item.title }}</span>
                </span>
                <span class="flex-shrink-0 text-xs text-[#C9CDD4]">{{ item.date }}</span>
              </RouterLink>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </section>
</template>
